// Loading a current sensor's readings for the energy page, as hourly buckets.
//
// Two sources, ONE shape (see energyAnalysis.js):
//   1. GetCurrentSenseHourly — the server buckets a month in ~3 s, ~260 KB.
//   2. Until that is deployed (HTML 404): every raw row, a day at a time
//      (~1.8 MB per day on a three-phase meter), bucketed here by the same rule.
//
// Finished days never change, so their buckets are cached — in memory for the
// session and in localStorage across launches (compact, newest KEEP_DAYS per
// sensor). Today is always fetched again.
//
// ⚠️ A FAILURE IS NOT AN EMPTY DAY (phase.js fetchDay learned this): a failed
// fetch is thrown, never cached as "no readings". An empty answer from a
// successful fetch IS cached — the sensor really recorded nothing that day.

import { getApplianceRuns, getCurrentSenseHourly, getCurrentSenseRows } from '../api/api';
import { addDays, bucketize, groupServerHours, ymd } from './energyAnalysis';
import { runsFromRows } from './applianceRuns';

const KEEP_DAYS = 75;
const PREFIX = 'energy.v1.';
const CHUNK_DAYS = 31;

const mem = new Map(); // `${uno}|${d}` -> day
let serverHasHourly = null; // null = not asked yet

const pad = (x) => String(x).padStart(2, '0');
const sqlDT = (dt) => `${ymd(dt)} ${pad(dt.getHours())}:${pad(dt.getMinutes())}:${pad(dt.getSeconds())}`;

// Compact on disk: a bucket as an array, in this order.
const F = ['h', 'p', 'n', 'wh', 'cov', 'mw', 'ma', 'vmin', 'vmax', 'vavg', 'sw', 'sva'];
const pack = (day) => JSON.stringify({ p: day.peak, b: day.hours.map((x) => F.map((k) => x[k])) });
const unpack = (d, s) => {
  const o = JSON.parse(s);
  return { d, peak: o.p || null, hours: o.b.map((a) => Object.fromEntries(F.map((k, i) => [k, a[i]]))) };
};

function readDisk(uno, d) {
  try {
    const s = localStorage.getItem(`${PREFIX}${uno}.${d}`);
    return s ? unpack(d, s) : null;
  } catch (e) {
    return null;
  }
}

function writeDisk(uno, day) {
  try {
    localStorage.setItem(`${PREFIX}${uno}.${day.d}`, pack(day));
  } catch (e) {
    // Full or blocked — the session cache still has it.
  }
}

/** Keep only the newest KEEP_DAYS cached days of this sensor. */
function pruneDisk(uno) {
  try {
    const mine = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const k = localStorage.key(i);
      if (k && k.startsWith(`${PREFIX}${uno}.`)) mine.push(k);
    }
    mine.sort().reverse().slice(KEEP_DAYS).forEach((k) => localStorage.removeItem(k));
  } catch (e) { /* storage unavailable */ }
}

const isHtml404 = (e) => e && e.response && e.response.status === 404;

async function fetchServer(uno, from, to) {
  const res = await getCurrentSenseHourly(uno, sqlDT(from), sqlDT(to));
  const body = res && res.data;
  if (!body || body.ok !== true || !Array.isArray(body.hours)) {
    throw new Error((body && body.message) || 'The server could not summarise the readings');
  }
  return groupServerHours(body);
}

async function fetchRawDay(uno, day) {
  const res = await getCurrentSenseRows(uno, `${ymd(day)} 00:00:00`, `${ymd(day)} 23:59:59`);
  if (!Array.isArray(res && res.data)) {
    throw new Error(`The server returned an error for ${ymd(day)}`);
  }
  return bucketize(res.data);
}

/**
 * Days [from, to) as day objects (absent = no readings that day).
 * opts.onProgress(doneDays, totalDays) — the raw fallback is slow.
 * -> { days: day[], source: 'server' | 'raw' | 'cache' }
 */
export async function loadDays(uno, from, to, opts = {}) {
  const today = ymd(new Date());
  const wanted = [];
  for (let dt = new Date(from); dt < to; dt = addDays(dt, 1)) wanted.push(new Date(dt));

  const got = new Map();
  const missing = [];
  wanted.forEach((dt) => {
    const d = ymd(dt);
    if (d < today) {
      const hit = mem.get(`${uno}|${d}`) || readDisk(uno, d);
      if (hit) {
        mem.set(`${uno}|${d}`, hit);
        got.set(d, hit);
        return;
      }
    }
    missing.push(dt);
  });

  let source = 'cache';
  const keep = (map, asked) => {
    asked.forEach((dt) => {
      const d = ymd(dt);
      const day = map.get(d) || { d, hours: [], peak: null };
      got.set(d, day);
      if (d < today) {
        mem.set(`${uno}|${d}`, day);
        writeDisk(uno, day);
      }
    });
  };

  // Contiguous runs of missing days, at most CHUNK_DAYS each.
  const runs = [];
  missing.forEach((dt) => {
    const last = runs[runs.length - 1];
    if (last && ymd(addDays(last[last.length - 1], 1)) === ymd(dt) && last.length < CHUNK_DAYS) last.push(dt);
    else runs.push([dt]);
  });

  let done = wanted.length - missing.length;
  if (opts.onProgress) opts.onProgress(done, wanted.length);

  for (const run of runs) {
    if (serverHasHourly !== false) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const map = await fetchServer(uno, run[0], addDays(run[run.length - 1], 1));
        serverHasHourly = true;
        source = 'server';
        keep(map, run);
        done += run.length;
        if (opts.onProgress) opts.onProgress(done, wanted.length);
        continue; // eslint-disable-line no-continue
      } catch (e) {
        if (!isHtml404(e)) throw e;
        serverHasHourly = false;
      }
    }
    source = 'raw';
    for (const dt of run) {
      // eslint-disable-next-line no-await-in-loop
      keep(await fetchRawDay(uno, dt), [dt]);
      done += 1;
      if (opts.onProgress) opts.onProgress(done, wanted.length);
    }
  }

  pruneDisk(uno);
  return { days: wanted.map((dt) => got.get(ymd(dt))).filter(Boolean), source };
}

/** Whether the fast server summary is deployed (null = not known yet). */
export const hourlyEndpointLive = () => serverHasHourly;

// ── One appliance's runs (applianceRuns.js), cached per finished day ───────

let serverHasRuns = null;

/**
 * Runs that START in [from, to) for an appliance alone on `phase`, at ≥ minW.
 * -> { runs: [{ s, e, n, wh, aw, mw, aa, pf }], source }
 */
export async function loadRuns(uno, phase, minW, from, to) {
  const today = ymd(new Date());
  const key = (d) => `${PREFIX}runs.${uno}.${phase}.${minW}.${d}`;
  const byDay = new Map();
  const missing = [];
  for (let dt = new Date(from); dt < to; dt = addDays(dt, 1)) {
    const d = ymd(dt);
    let hit = null;
    if (d < today) {
      try { hit = JSON.parse(localStorage.getItem(key(d)) || 'null'); } catch (e) { hit = null; }
    }
    if (hit) byDay.set(d, hit);
    else missing.push(new Date(dt));
  }

  const keep = (d, runs) => {
    byDay.set(d, runs);
    if (d < today) {
      try { localStorage.setItem(key(d), JSON.stringify(runs)); } catch (e) { /* full */ }
    }
  };
  const splitByDay = (runs, days) => {
    const m = new Map(days.map((dt) => [ymd(dt), []]));
    runs.forEach((r) => { const d = r.s.slice(0, 10); if (m.has(d)) m.get(d).push(r); });
    m.forEach((v, d) => keep(d, v));
  };

  let source = 'cache';
  // One request for the whole missing span (≤ 45 days on the server).
  if (missing.length && serverHasRuns !== false) {
    try {
      const res = await getApplianceRuns(uno, phase, minW,
        `${ymd(missing[0])} 00:00:00`, `${ymd(addDays(missing[missing.length - 1], 1))} 00:00:00`);
      const body = res && res.data;
      if (!body || body.ok !== true || !Array.isArray(body.runs)) {
        throw new Error((body && body.message) || 'The server could not list the runs');
      }
      serverHasRuns = true;
      source = 'server';
      splitByDay(body.runs, missing);
      missing.length = 0;
    } catch (e) {
      if (!isHtml404(e)) throw e;
      serverHasRuns = false;
    }
  }
  for (const dt of missing) {
    // eslint-disable-next-line no-await-in-loop
    const res = await getCurrentSenseRows(uno, `${ymd(dt)} 00:00:00`, `${ymd(dt)} 23:59:59`);
    if (!Array.isArray(res && res.data)) throw new Error(`The server returned an error for ${ymd(dt)}`);
    keep(ymd(dt), runsFromRows(res.data, phase, minW));
    source = 'raw';
  }

  const runs = [...byDay.entries()].sort((a, b) => (a[0] < b[0] ? -1 : 1)).flatMap((e) => e[1]);
  return { runs, source };
}

// ── Period totals for the history table — tiny, kept forever ───────────────

const PERIOD_PREFIX = 'energy.v1.period.';

export function readPeriodTotal(uno, startYmd) {
  try {
    const s = localStorage.getItem(`${PERIOD_PREFIX}${uno}.${startYmd}`);
    return s ? JSON.parse(s) : null;
  } catch (e) {
    return null;
  }
}

/** Only for a period that has ENDED — a running one changes every 30 s. */
export function writePeriodTotal(uno, startYmd, total) {
  try {
    localStorage.setItem(`${PERIOD_PREFIX}${uno}.${startYmd}`, JSON.stringify(total));
  } catch (e) { /* storage unavailable */ }
}

// ── The live strip: the newest reading of each phase ───────────────────────

/** -> { at: Date|null, phases: { [p]: { v, a, w, pf } } } from the last 3 minutes. */
export async function loadLatest(uno) {
  const now = new Date();
  const res = await getCurrentSenseRows(uno, sqlDT(new Date(now - 3 * 60000)), sqlDT(addDays(now, 1)));
  const rows = Array.isArray(res && res.data) ? res.data : [];
  const latest = {};
  let at = null;
  rows.forEach((r) => {
    const t = new Date(String(r.DateTime).replace('Z', '').replace('T', ' '));
    const p = Number(r.Phase ?? r.phase) || 1;
    if (!latest[p] || t > latest[p].t) {
      const v = Number(r.voltage) || 0;
      const a = Number(r.current) || 0;
      const w = Number(r.power) || 0;
      latest[p] = {
        t, v, a, w, pf: v > 0 && a > 0.05 ? Math.min(1, w / (v * a)) : null,
      };
    }
    if (!at || t > at) at = t;
  });
  return { at, phases: latest };
}
